import { GoogleCaptchaContextProps } from '@/types/context';
import { useState, useEffect, createContext, useContext } from 'react';

// Define the site key
const siteKey = process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY || '';

// Create the context
const GoogleCaptchaContext = createContext<
  GoogleCaptchaContextProps | undefined
>(undefined);

export default function GoogleCaptchaProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [isScriptLoaded, setIsScriptLoaded] = useState(false);

  // Function to execute reCAPTCHA and get the token
  const executeRecaptcha = async (action = 'default'): Promise<string> => {
    if (!window.grecaptcha) {
      throw new Error('reCAPTCHA has not been loaded');
    }

    try {
      const token = await window.grecaptcha.execute(siteKey, { action });
      return token;
    } catch (error) {
      console.error('reCAPTCHA execution failed:', error);
      throw error;
    }
  };

  useEffect(() => {
    if (isScriptLoaded) return;

    const loadRecaptcha = () => {
      const script = document.createElement('script');
      script.src = `https://www.google.com/recaptcha/api.js?render=${siteKey}`;
      script.async = true;
      script.defer = true;
      script.addEventListener('load', () => {
        if (window.grecaptcha) {
          window.grecaptcha.ready(() => {
            setIsScriptLoaded(true);
          });
        }
      });
      document.head.appendChild(script);
    };

    const script = document.querySelector(
      `script[src="https://www.google.com/recaptcha/api.js?render=${siteKey}"]`
    );

    if (!script) {
      loadRecaptcha();
    } else {
      setIsScriptLoaded(true);
    }

    return () => {
      const existingScript = document.querySelector(
        `script[src="https://www.google.com/recaptcha/api.js?render=${siteKey}"]`
      );
      if (existingScript) {
        existingScript.remove();
      }
    };
  }, [isScriptLoaded]);

  // Create the context value
  const contextValue = {
    executeRecaptcha,
    isScriptLoaded,
  };

  // Return the provider
  return (
    <GoogleCaptchaContext.Provider value={contextValue}>
      {children}
    </GoogleCaptchaContext.Provider>
  );
}

// Create a custom hook to use the context
export function useGoogleCaptcha() {
  const context = useContext(GoogleCaptchaContext);
  if (context === undefined) {
    throw new Error(
      'useGoogleCaptcha must be used within a GoogleCaptchaProvider'
    );
  }
  return context;
}
